import { EventConsts, UnsavedChangesModalConsts } from 'client/util/Constants';
import LoggingService from 'client/util/LoggingService';
import modalUtil from 'client/AdminPortal/util/ModalUtil';

export const missingMethodMessage = 'The RouteGuardMixin requires that the importing component define the hasModelChanged() method!';

/**
 * @mixin This mixin should be used by Vue components rendered by Vue Router so that the
 * beforeRouteLeave life cycle method is available and they can prevent navigation away from the
 * page they're rendered on if they have unsaved changes. The components that use this mixin are
 * required to implement the hasModelChanged() method. This method is usually implemented by the
 * AsidePanel and calls actions mapped from a Vuex model.
 * @see https://github.com/TheJumpCloud/jumpcloud-admin-portal/blob/master/client/Radius/containers/RadiusAsidePanel.vue#L180
 */
export function createRouteGuardMixin(
  myModalUtil = modalUtil,
) {
  return {
    data() {
      return {
        routeGuardAllowContinue: false,
        allowSubRoutesNavigation: '',
      };
    },

    mounted() {
      this.handleRouteGuardMixinMounted();
      this.addOnBeforeUnload();
    },

    destroyed() {
      this.clearOnBeforeUnload();
    },

    beforeRouteLeave(to, from, next) {
      this.handleBeforeRouteLeave(to, from, next);
    },

    methods: {
      handleBeforeRouteLeave(to, from, next) {
        LoggingService.debug('RouteGuardMixin.handleBeforeRouteLeave', {
          to, from, routeGuardAllowContinue: this.routeGuardAllowContinue,
        });

        if (this.hasModelChanged()
          && !this.routeGuardAllowContinue
          && !this.isSubRouteNavigation(from.fullPath, to.fullPath)) {
          myModalUtil.unSavedChangesModal((canContinue) => {
            this.handleRouteGuardModalResult(canContinue, to.fullPath);
          });
          next(false);
        } else {
          // reset allow modal state
          this.routeGuardAllowContinue = false;
          next();
        }
      },

      handleRouteGuardModalResult(canContinue = true, location) {
        LoggingService.debug('RouteGuardMixin.handleRouteGuardModalResult: ', { canContinue, location });
        this.routeGuardAllowContinue = !!canContinue;
        if (canContinue) {
          this.$router.push(location);
        }
      },

      handleOnBeforeUnload(e) {
        if (this.hasModelChanged()) {
          e.preventDefault();
          e.returnValue = UnsavedChangesModalConsts;
          return UnsavedChangesModalConsts;
        }
        return undefined;
      },

      addOnBeforeUnload() {
        this.handleOnBeforeUnload = this.handleOnBeforeUnload.bind(this);
        window.addEventListener(EventConsts.beforeunload, this.handleOnBeforeUnload);
      },

      clearOnBeforeUnload() {
        window.removeEventListener(EventConsts.beforeunload, this.handleOnBeforeUnload);
      },

      handleRouteGuardMixinMounted() {
        if (typeof this.hasModelChanged !== 'function') {
          throw new Error(missingMethodMessage);
        }
      },

      /**
       * Checks if navigation is occurring within sub routes within the provided main route
       * This helps support pages that might have internal asides, but we still want to
       * avoid leaving the main page altogether with unsaved changes.
       * Examples:
       *  Given allowSubRoutesNavigation: 'security/authnpolicies/settings'
       *  Navigation results:
       *    from: 'security/authnpolicies/settings'
       *    to: 'security/authnpolicies/settings/trustedApp/new'
       *    Result: true
       *
       *    from: 'security/authnpolicies/settings'
       *    to: 'security/iplists'
       *    Result: false
       *
       * @param fromPath route 'from.fullPath' value
       * @param toPath route 'to.fullPath' value
       * @returns {boolean} true only if allowSubRoutesNavigation set
       * and both param routes match the route set in allowSubRoutesNavigation
       */
      isSubRouteNavigation(fromPath, toPath) {
        return !!(this.allowSubRoutesNavigation
          && fromPath.includes(this.allowSubRoutesNavigation)
          && toPath.includes(this.allowSubRoutesNavigation));
      },
    },
  };
}

export default createRouteGuardMixin();
